import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  GridDetailField,
  GridDetailFieldsGrid,
  GridDetailFieldsGridFull,
  GridDetailPopup,
  OPS_GRID_TOOLBAR_DESKTOP_ONLY,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  createRemoteGridFilterOptionChangedHandler,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridDetailPopupSizeKey,
  type OpsGridToolbarMenuItem
} from '../opsDataGrid';
import { getActivityStatusPillClassName } from './activityStatusPill';
import {
  formatActivityDateTime,
  formatEndReason,
  formatSessionDuration,
  formatThemeLabel,
  formatUserLabel,
  formatViewport
} from './formatters';
import type { UserActivitySessionEntry, UserActivitySessionLoadRequest } from './types';

export interface UserActivityHistoryGridProps {
  loadPage: (request: UserActivitySessionLoadRequest) => Promise<{ total: number; items: UserActivitySessionEntry[] }>;
  pageSize?: number;
  stateStorageKey: string;
  showTenantColumn?: boolean;
  /** When set, renders an Activity summary button in the grid toolbar. */
  onShowSummary?: () => void;
  summaryButtonText?: string;
  /** ISO range bounds applied to StartedAt (from shared analytics toolbar). */
  rangeFrom?: string;
  rangeTo?: string;
  /** Pre-applied DevExtreme filter (report drill-down). */
  initialFilter?: unknown;
  /** Change token so the same filter object can be re-applied. */
  filterNonce?: string;
}

export const USER_ACTIVITY_HISTORY_GRID_SCHEMA_VERSION = 3;

export const USER_ACTIVITY_HISTORY_TENANT_COLUMN = 'tenantId' as const;

export function userActivityHistoryGridColumns(showTenantColumn: boolean): string[] {
  const columns: string[] = [
    'startedAt',
    'lastSeenAt',
    'duration',
    'status',
    'displayName'
  ];
  if (showTenantColumn) columns.push(USER_ACTIVITY_HISTORY_TENANT_COLUMN);
  columns.push(
    'roles',
    'ipAddress',
    'viewportBucket',
    'viewportWidth',
    'screenBucket',
    'touchCapable',
    'geoResolution',
    'geoCountry',
    'geoCountryCode',
    'geoContinent',
    'geoContinentCode',
    'geoAsn',
    'geoAsName',
    'geoAsDomain',
    'geoResolvedAt',
    'timezone',
    'locale',
    'lastPath',
    'appBuild',
    'themeFamily',
    'maxTouchPoints',
    'deviceCategory',
    'browser',
    'os',
    'endReason',
    'endedAt'
  );
  return columns;
}

function sizeBucket(width: number | null, height: number | null): string {
  if (width == null || height == null) return 'Unknown';
  const max = Math.max(width, height);
  if (max < 768) return 'Compact (<768)';
  if (max < 1024) return 'Small (768–1023)';
  if (max < 1440) return 'Medium (1024–1439)';
  if (max < 1920) return 'Large (1440–1919)';
  return 'XLarge (1920+)';
}

function sessionStatus(endedAt: string | null): string {
  return endedAt ? 'Completed' : 'Open';
}

function touchCapable(maxTouchPoints: number | null): string {
  return (maxTouchPoints ?? 0) > 0 ? 'Touch' : 'No touch';
}

function geoResolution(geoResolvedAt: string | null): string {
  return geoResolvedAt ? 'Resolved' : 'Unresolved';
}

type SortDescriptor = {
  selector?: unknown;
  desc?: boolean;
};

function sessionDurationMs(
  startedAt: string,
  endedAt: string | null,
  lastSeenAt: string
): number {
  const start = Date.parse(startedAt);
  const end = Date.parse(endedAt ?? lastSeenAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.max(0, end - start);
}

function getFirstSort(sort: unknown): SortDescriptor | null {
  if (!sort) return null;
  if (Array.isArray(sort)) return (sort[0] as SortDescriptor | undefined) ?? null;
  return sort as SortDescriptor;
}

function buildLoadRequest(
  options: {
    skip?: number;
    take?: number;
    searchValue?: unknown;
    filter?: unknown;
    sort?: unknown;
  },
  fallbackTake: number,
  range?: { from?: string; to?: string }
): UserActivitySessionLoadRequest {
  const sort = getFirstSort(options.sort);
  const selector = typeof sort?.selector === 'string' ? sort.selector : undefined;
  const search = typeof options.searchValue === 'string' ? options.searchValue.trim() : '';
  return {
    skip: typeof options.skip === 'number' ? options.skip : 0,
    take: typeof options.take === 'number' ? options.take : fallbackTake,
    search: search || undefined,
    filter: options.filter ? JSON.stringify(options.filter) : undefined,
    sortBy: selector,
    sortDesc: sort?.desc,
    from: range?.from,
    to: range?.to
  };
}

function EndReasonBadge({ endReason }: { endReason: string | null }) {
  return (
    <span className={getActivityStatusPillClassName(endReason)}>{formatEndReason(endReason)}</span>
  );
}

function UserActivitySessionDetailView({
  session,
  showTenantColumn
}: {
  session: UserActivitySessionEntry;
  showTenantColumn: boolean;
}) {
  return (
    <>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ops-color-text, #111827)' }}>
          {formatUserLabel(session.displayName, session.userName)}
        </div>
        <div style={{ marginTop: 10 }}>
          <EndReasonBadge endReason={session.endReason} />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ops-color-muted, #64748b)', fontFamily: 'monospace' }}>
          {session.sessionKey}
        </div>
      </div>

      <TabPanel
        className="ops-grid-detail-tab-panel"
        deferRendering={false}
        animationEnabled={false}
        swipeEnabled={false}
        showNavButtons={false}
      >
        <TabItem title="Session">
          <GridDetailFieldsGrid>
            <GridDetailField label="Started">{formatActivityDateTime(session.startedAt)}</GridDetailField>
            <GridDetailField label="Last seen">{formatActivityDateTime(session.lastSeenAt)}</GridDetailField>
            <GridDetailField label="Ended" muted={!session.endedAt}>
              {formatActivityDateTime(session.endedAt)}
            </GridDetailField>
            <GridDetailField label="Duration">
              {formatSessionDuration(session.startedAt, session.endedAt, session.lastSeenAt)}
            </GridDetailField>
            <GridDetailFieldsGridFull>
              <GridDetailField label="Last observed path" muted={!session.lastPath}>
                {session.lastPath ?? '—'}
              </GridDetailField>
            </GridDetailFieldsGridFull>
            <GridDetailField label="Roles" muted={!session.roles}>
              {session.roles ?? '—'}
            </GridDetailField>
            {showTenantColumn ? (
              <GridDetailField label="Tenant" muted={!session.tenantId}>
                {session.tenantId ?? '—'}
              </GridDetailField>
            ) : null}
          </GridDetailFieldsGrid>
        </TabItem>
        <TabItem title="Client">
          <GridDetailFieldsGrid>
            <GridDetailField label="IP" muted={!session.ipAddress}>
              {session.ipAddress ?? '—'}
            </GridDetailField>
            <GridDetailField label="Browser" muted={!session.browser}>
              {session.browser ?? '—'}
            </GridDetailField>
            <GridDetailField label="OS" muted={!session.os}>
              {session.os ?? '—'}
            </GridDetailField>
            <GridDetailField label="Device">{session.deviceCategory ?? '—'}</GridDetailField>
            <GridDetailField label="Viewport">
              {formatViewport(session.viewportWidth, session.viewportHeight)}
            </GridDetailField>
            <GridDetailField label="Screen" muted={!session.screenWidth && !session.screenHeight}>
              {session.screenWidth && session.screenHeight
                ? `${session.screenWidth} × ${session.screenHeight}`
                : '—'}
            </GridDetailField>
            <GridDetailField label="Theme">
              {formatThemeLabel(session.themeFamily, session.themeMode)}
            </GridDetailField>
            <GridDetailField label="Locale" muted={!session.locale}>
              {session.locale ?? '—'}
            </GridDetailField>
            <GridDetailField label="Timezone" muted={!session.timezone}>
              {session.timezone ?? '—'}
            </GridDetailField>
            <GridDetailField label="Build" muted={!session.appBuild}>
              {session.appBuild ?? '—'}
            </GridDetailField>
            {session.userAgent ? (
              <GridDetailFieldsGridFull>
                <GridDetailField label="User agent">
                  <pre className="ops-grid-detail-mono-block">{session.userAgent}</pre>
                </GridDetailField>
              </GridDetailFieldsGridFull>
            ) : null}
          </GridDetailFieldsGrid>
        </TabItem>
        <TabItem title="Geo">
          <GridDetailFieldsGrid>
            <GridDetailField label="Country" muted={!session.geoCountry}>
              {session.geoCountry ?? '—'}
            </GridDetailField>
            <GridDetailField label="Country code" muted={!session.geoCountryCode}>
              {session.geoCountryCode ?? '—'}
            </GridDetailField>
            <GridDetailField label="Continent" muted={!session.geoContinent}>
              {session.geoContinent ?? '—'}
            </GridDetailField>
            <GridDetailField label="ASN" muted={!session.geoAsn}>
              {session.geoAsn ?? '—'}
            </GridDetailField>
            <GridDetailField label="AS name" muted={!session.geoAsName}>
              {session.geoAsName ?? '—'}
            </GridDetailField>
            <GridDetailField label="AS domain" muted={!session.geoAsDomain}>
              {session.geoAsDomain ?? '—'}
            </GridDetailField>
            <GridDetailField label="Geo resolved" muted={!session.geoResolvedAt}>
              {formatActivityDateTime(session.geoResolvedAt)}
            </GridDetailField>
          </GridDetailFieldsGrid>
        </TabItem>
      </TabPanel>
    </>
  );
}

export function UserActivityHistoryGrid({
  loadPage,
  pageSize = 100,
  stateStorageKey,
  showTenantColumn = false,
  onShowSummary,
  summaryButtonText = 'Activity summary',
  rangeFrom,
  rangeTo,
  initialFilter = null,
  filterNonce = ''
}: UserActivityHistoryGridProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [viewSession, setViewSession] = useState<UserActivitySessionEntry | null>(null);
  const gridColumns = useMemo(
    () => userActivityHistoryGridColumns(showTenantColumn),
    [showTenantColumn]
  );

  const rangeKey = `${rangeFrom ?? ''}|${rangeTo ?? ''}`;
  const filterKey = `${filterNonce}|${JSON.stringify(initialFilter ?? null)}`;

  const dataSource = useMemo(() => {
    const store = new CustomStore<UserActivitySessionEntry, string>({
      key: 'id',
      load: async (options) => {
        const page = await loadPage(
          buildLoadRequest(options, pageSize, { from: rangeFrom, to: rangeTo })
        );
        return { data: page.items, totalCount: page.total };
      }
    });
    return new DataSource({ store, pageSize, paginate: true });
  }, [loadPage, pageSize, rangeFrom, rangeTo]);

  const rangeKeyRef = useRef(rangeKey);
  useEffect(() => {
    const prev = rangeKeyRef.current;
    rangeKeyRef.current = rangeKey;
    if (prev === rangeKey) return;
    dataSource.pageIndex(0);
    void dataSource.reload();
  }, [rangeKey, dataSource]);

  useEffect(() => {
    const grid = gridRef.current?.instance();
    if (!grid) return;
    grid.clearFilter();
    if (initialFilter != null) {
      grid.filter(initialFilter as never);
    }
    dataSource.pageIndex(0);
    void dataSource.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply when drill-down token changes
  }, [filterKey, dataSource]);

  const onRemoteFilterOptionChanged = useMemo(
    () => createRemoteGridFilterOptionChangedHandler(dataSource),
    [dataSource]
  );

  const toolbarExtraMenuItems = useMemo((): OpsGridToolbarMenuItem[] => {
    if (!onShowSummary) return [];
    return [{ id: 'show-summary', text: summaryButtonText, icon: 'chart', onClick: onShowSummary }];
  }, [onShowSummary, summaryButtonText]);

  return (
    <>
      <DataGrid
        ref={gridRef}
        dataSource={dataSource}
        keyExpr="id"
        height="100%"
        width="100%"
        showBorders
        columnAutoWidth
        columnHidingEnabled
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        {...OPS_SITE_GRID_PROPS}
        wordWrapEnabled
        remoteOperations={{ paging: true, filtering: true, sorting: true }}
        onOptionChanged={onRemoteFilterOptionChanged}
        onRowDblClick={(e) => onGridRowDblClick(e, setViewSession)}
        onKeyDown={(e) => onGridFocusedRowEnter(e, setViewSession)}
      >
        <OpsGridStateStoring
          storageKey={stateStorageKey}
          schemaVersion={USER_ACTIVITY_HISTORY_GRID_SCHEMA_VERSION}
          columns={gridColumns}
        />
        <Toolbar>
          {onShowSummary ? (
            <Item
              location="before"
              cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}
              widget="dxButton"
              options={{
                text: summaryButtonText,
                icon: 'chart',
                stylingMode: 'outlined',
                onClick: onShowSummary
              }}
            />
          ) : null}
          <OpsGridToolbarItems
            gridRef={gridRef}
            onRefresh={() => void dataSource.reload()}
            refreshHint="Reload sessions"
            extraMenuItems={toolbarExtraMenuItems}
            export={{
              fileName: 'user-activity-export',
              worksheetName: 'User activity',
              hint: 'Export sessions'
            }}
            resetLayout={{ storageKey: stateStorageKey }}
          />
          <Item name="searchPanel" location="after" />
        </Toolbar>
        <SearchPanel visible width={240} placeholder="Search sessions…" />
        <HeaderFilter visible />
        <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
        <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
        <Paging defaultPageSize={pageSize} />
        <Pager
          visible
          showInfo
          showNavigationButtons
          showPageSizeSelector
          allowedPageSizes={[50, 100, 250, 500]}
          displayMode="full"
        />
        <Column
          name="startedAt"
          dataField="startedAt"
          caption="Started"
          dataType="datetime"
          sortOrder="desc"
          width={170}
          calculateDisplayValue={(row: UserActivitySessionEntry) => formatActivityDateTime(row.startedAt)}
        />
        <Column
          name="lastSeenAt"
          dataField="lastSeenAt"
          caption="Last seen"
          dataType="datetime"
          width={170}
          calculateDisplayValue={(row: UserActivitySessionEntry) => formatActivityDateTime(row.lastSeenAt)}
        />
        <Column
          name="duration"
          caption="Duration"
          width={110}
          allowHeaderFiltering={false}
          calculateCellValue={(row: UserActivitySessionEntry) =>
            sessionDurationMs(row.startedAt, row.endedAt, row.lastSeenAt)}
          calculateDisplayValue={(row: UserActivitySessionEntry) =>
            formatSessionDuration(row.startedAt, row.endedAt, row.lastSeenAt)}
        />
        <Column
          name="status"
          caption="Status"
          width={110}
          calculateCellValue={(row: UserActivitySessionEntry) => sessionStatus(row.endedAt)}
          calculateDisplayValue={(row: UserActivitySessionEntry) => sessionStatus(row.endedAt)}
          cellRender={(c) => {
            const row = c.data as UserActivitySessionEntry;
            if (!row.endedAt) {
              return <span className={getActivityStatusPillClassName(null)}>Open</span>;
            }
            return <EndReasonBadge endReason={row.endReason} />;
          }}
        />
        <Column
          name="displayName"
          dataField="displayName"
          caption="User"
          minWidth={180}
          calculateCellValue={(row: UserActivitySessionEntry) =>
            formatUserLabel(row.displayName, row.userName)}
          calculateDisplayValue={(row: UserActivitySessionEntry) =>
            formatUserLabel(row.displayName, row.userName)}
        />
        <Column name="roles" dataField="roles" caption="Roles" minWidth={140} />
        {showTenantColumn ? <Column name="tenantId" dataField="tenantId" caption="Tenant" minWidth={200} /> : null}
        <Column name="ipAddress" dataField="ipAddress" caption="IP" width={130} />
        <Column
          name="viewportBucket"
          caption="Viewport bucket"
          width={140}
          visible={false}
          calculateCellValue={(row: UserActivitySessionEntry) =>
            sizeBucket(row.viewportWidth, row.viewportHeight)}
          calculateDisplayValue={(row: UserActivitySessionEntry) =>
            sizeBucket(row.viewportWidth, row.viewportHeight)}
        />
        <Column
          name="viewportWidth"
          dataField="viewportWidth"
          caption="Viewport"
          width={110}
          allowHeaderFiltering={false}
          calculateCellValue={(row: UserActivitySessionEntry) =>
            formatViewport(row.viewportWidth, row.viewportHeight)}
          calculateDisplayValue={(row: UserActivitySessionEntry) =>
            formatViewport(row.viewportWidth, row.viewportHeight)}
        />
        <Column
          name="screenBucket"
          caption="Screen bucket"
          width={140}
          visible={false}
          calculateCellValue={(row: UserActivitySessionEntry) =>
            sizeBucket(row.screenWidth, row.screenHeight)}
          calculateDisplayValue={(row: UserActivitySessionEntry) =>
            sizeBucket(row.screenWidth, row.screenHeight)}
        />
        <Column
          name="touchCapable"
          caption="Touch capable"
          width={110}
          visible={false}
          calculateCellValue={(row: UserActivitySessionEntry) => touchCapable(row.maxTouchPoints)}
          calculateDisplayValue={(row: UserActivitySessionEntry) => touchCapable(row.maxTouchPoints)}
        />
        <Column
          name="geoResolution"
          caption="Geo resolution"
          width={120}
          visible={false}
          calculateCellValue={(row: UserActivitySessionEntry) => geoResolution(row.geoResolvedAt)}
          calculateDisplayValue={(row: UserActivitySessionEntry) => geoResolution(row.geoResolvedAt)}
        />
        <Column dataField="geoCountry" caption="Country" minWidth={120} />
        <Column dataField="geoCountryCode" caption="Country code" width={90} visible={false} />
        <Column dataField="geoContinent" caption="Continent" minWidth={110} visible={false} />
        <Column dataField="geoContinentCode" caption="Continent code" width={90} visible={false} />
        <Column dataField="geoAsn" caption="ASN" width={100} visible={false} />
        <Column dataField="geoAsName" caption="AS name" minWidth={140} visible={false} />
        <Column dataField="geoAsDomain" caption="AS domain" minWidth={120} visible={false} />
        <Column
          dataField="geoResolvedAt"
          caption="Geo resolved"
          width={170}
          visible={false}
          calculateDisplayValue={(row: UserActivitySessionEntry) => formatActivityDateTime(row.geoResolvedAt)}
        />
        <Column dataField="timezone" caption="Timezone" minWidth={140} visible={false} />
        <Column dataField="locale" caption="Locale" width={90} visible={false} />
        <Column dataField="lastPath" caption="Last observed path" minWidth={160} visible={false} />
        <Column dataField="appBuild" caption="Build" width={100} visible={false} />
        <Column
          dataField="themeFamily"
          caption="Theme"
          width={120}
          visible={false}
          calculateCellValue={(row: UserActivitySessionEntry) =>
            formatThemeLabel(row.themeFamily, row.themeMode)}
          calculateDisplayValue={(row: UserActivitySessionEntry) =>
            formatThemeLabel(row.themeFamily, row.themeMode)}
        />
        <Column dataField="maxTouchPoints" caption="Touch" width={70} visible={false} />
        <Column dataField="deviceCategory" caption="Device" width={100} />
        <Column dataField="browser" caption="Browser" minWidth={120} />
        <Column dataField="os" caption="OS" minWidth={120} />
        <Column
          dataField="endReason"
          caption="Status"
          width={120}
          cellRender={(c) => <EndReasonBadge endReason={(c.data as UserActivitySessionEntry).endReason} />}
        />
        <Column
          dataField="endedAt"
          caption="Ended"
          width={170}
          calculateDisplayValue={(row: UserActivitySessionEntry) => formatActivityDateTime(row.endedAt)}
        />
      </DataGrid>

      <GridDetailPopup
        visible={viewSession !== null}
        title="Session details"
        onClose={() => setViewSession(null)}
        width={640}
        sizeStorageKey={opsGridDetailPopupSizeKey(stateStorageKey)}
      >
        {viewSession ? (
          <UserActivitySessionDetailView session={viewSession} showTenantColumn={showTenantColumn} />
        ) : null}
      </GridDetailPopup>
    </>
  );
}
